import { actions } from "@app/utils";
import { createApiClient } from "@app/utils/apiClient";

export const storiesClient = createApiClient(
  actions["exd-storyteller-ai/stories"],
);

function debounce(func, wait) {
  var timeout, value;
  return function () {
    if (!timeout) value = func.apply(this, arguments);
    clearTimeout(timeout);
    timeout = setTimeout(() => {
      timeout = value = null;
    }, wait);
    return value;
  };
}

/**
 * Fetches all stories.
 * @param {Object} options - Additional options for the request.
 *
 * @returns {Promise<Array>} A promise that resolves to an array of stories.
 * @throws {Error} If the request fails.
 */
export const getStories = async (options = {}) => {
  const response = await storiesClient.get("/", {}, options);

  if (!response.ok) {
    throw new Error("Failed to fetch stories", { cause: response });
  }

  return response.data?.stories ?? [];
};

/**
 * Fetches a specific story by its ID.
 *
 * @param {string} id - The ID of the story to fetch.
 * @param {Object} options - Additional options for the request.
 *
 * @returns {Promise<Object>} A promise that resolves to the story object.
 * @throws {Error} If the request fails.
 */
export const getStory = async (id, options = {}) => {
  const response = await storiesClient.get(`/${id}`, {}, options);

  if (!response.ok) {
    throw new Error(`Failed to fetch story ${id}`, { cause: response });
  }

  return response.data?.story;
};

/**
 * Creates a new story.
 *
 * @param {Object} story - The story data to create.
 * @param {Object} options - Additional options for the request.
 *
 * @returns {Promise<Object>} - A promise that resolves to the created story.
 * @throws {Error} If the request fails.
 */
export const createStory = async (story, options = {}) => {
  const response = await storiesClient.post(
    "/",
    {
      story,
    },
    options,
  );

  if (!response.ok) {
    throw new Error("Failed to create story", { cause: response });
  }

  return response.data?.story;
};

/**
 * Updates an existing story.
 *
 * @param {string} id - The ID of the story to update.
 * @param {Object} story - The updated story data.
 * @param {Object} options - Additional options for the request.
 *
 * @returns {Promise<Object>} A promise that resolves to the updated story.
 * @throws {Error} If the request fails.
 */
export const updateStory = async (id, story, options = {}) => {
  // const { queryKey, signal } = context;
  // const [, , , id] = queryKey || [];

  if (!id) {
    throw new Error("Story ID is required to update a story");
  }

  const response = await storiesClient.put(`/${id}`, story, options);

  if (!response.ok) {
    throw new Error(`Failed to update story ${id}`, { cause: response });
  }

  return response.data?.story;
};

export const updateStoryDebounced = debounce(updateStory, 5000);

/**
 * Deletes a story by its ID.
 *
 * @param {string} id - The ID of the story to delete.
 * @param {Object} options - Additional options for the request.
 *
 * @returns {Promise<Object>} A promise that resolves to the deleted story data.
 * @throws {Error} If the request fails.
 */
export const deleteStory = async (id, options = {}) => {
  const response = await storiesClient.delete(`/${id}`, {}, options);

  if (!response.ok) {
    throw new Error(`Failed to delete story ${id}`, { cause: response });
  }

  return response.data;
};

/**
 * Shares a story with a specific user.
 *
 * @param {string} id - The ID of the story to share.
 * @param {string} email - The email address of the user to share the story with.
 * @param {Object} options - Additional options for the request.
 *
 * @returns {Promise<Object>} A promise that resolves to the shared story data.
 * @throws {Error} If the request fails.
 */
export const shareStory = async (id, email, options = {}) => {
  const response = await storiesClient.put(
    `/${id}/share`,
    {
      shareWith: email,
    },
    options,
  );

  if (!response.ok) {
    throw new Error(`Failed to share story ${id}`, { cause: response });
  }

  return response.data?.story;
};

/**
 * Unshares a story from a specific user.
 *
 * @param {string} id - The ID of the story to unshare.
 * @param {string} email - The email address of the user to unshare the story from.
 * @param {Object} options - Additional options for the request.
 *
 * @returns {Promise<Object>} A promise that resolves to the unshared story data.
 * @throws {Error} If the request fails.
 */
export const unshareStory = async (id, email, options = {}) => {
  const response = await storiesClient.put(
    `/${id}/unshare`,
    {
      shareWith: email,
    },
    options,
  );

  if (!response.ok) {
    throw new Error(`Failed to unshare story ${id}`, { cause: response });
  }

  return response.data?.story;
};
